import React, { useState, useEffect, useRef } from "react";
import Sidebar from "./Sidebar";
import Navbar from "./Navbar";
import { useNavigate, Link } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import {
  FiArrowLeft,
  FiChevronRight,
  FiEye,
  FiUsers,
  FiSearch,
  FiCopy,
  FiTrash2,
  FiMail,
  FiSend,
  FiX,
  FiAlertCircle,
  FiRefreshCw,
  FiBell,
} from "react-icons/fi";
import SendPushModal from "./SendPushModal";

const baseUrl = process.env.REACT_APP_API_BASE_URL;

const avatarColors = [
  "bg-[#ffffcc] text-[#999900]",
  "bg-purple-100 text-purple-600",
  "bg-green-100 text-green-600",
  "bg-orange-100 text-orange-600",
  "bg-rose-100 text-rose-600",
  "bg-teal-100 text-teal-600",
];

const getAvatarColor = (name = "") => avatarColors[name.charCodeAt(0) % avatarColors.length];

const UserList = () => {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filter] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [itemsPerPage] = useState(10);
  const navigate = useNavigate();
  const debounceTimer = useRef(null);

  // Delete modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedUserForDelete, setSelectedUserForDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Send Email Modal state
  const [mailModalOpen, setMailModalOpen] = useState(false);
  const [selectedUserForMail, setSelectedUserForMail] = useState(null);
  const [mailSubject, setMailSubject] = useState("");
  const [mailContent, setMailContent] = useState("");
  const [mailLoading, setMailLoading] = useState(false);

  // Push Notification Modal state
  const [pushModalOpen, setPushModalOpen] = useState(false);
  const [selectedUserForPush, setSelectedUserForPush] = useState(null);
  const [pushModalMode, setPushModalMode] = useState("bulk");

  const handleOpenPushModal = (user = null) => {
    if (user) {
      setSelectedUserForPush(user);
      setPushModalMode("particular");
    } else {
      setSelectedUserForPush(null);
      setPushModalMode("bulk");
    }
    setPushModalOpen(true);
  };

  const handleOpenMailModal = (user) => {
    if (!user || !user.email) {
      toast.error("User does not have an email address");
      return;
    }
    setSelectedUserForMail(user);
    setMailSubject(`Notice from Zeta Network - Account #${user.userId || user.id}`);
    setMailContent(`Hello ${user.username || "User"},

We are reaching out to you regarding your Zeta Network account (#${user.userId || user.id}).

If you have any questions or require assistance with mining or rewards, please feel free to reach out.

Best regards,
Zeta Admin Team`);
    setMailModalOpen(false);
    setTimeout(() => setMailModalOpen(true), 50);
  };

  const handleSendEmail = async (e) => {
    if (e) e.preventDefault();
    if (!selectedUserForMail || !selectedUserForMail.email) {
      toast.error("User email not available");
      return;
    }
    if (!mailSubject.trim() || !mailContent.trim()) {
      toast.error("Subject and message content are required");
      return;
    }

    setMailLoading(true);
    const token = localStorage.getItem("token");
    const payload = {
      email: selectedUserForMail.email,
      userId: selectedUserForMail.userId || selectedUserForMail.id,
      subject: mailSubject.trim(),
      content: mailContent.trim(),
      message: mailContent.trim(),
    };

    try {
      let sent = false;
      let errorMsg = "";

      // 1. Try /users/email/warning
      try {
        const r1 = await fetch(`${baseUrl}/users/email/warning`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        });
        const d1 = await r1.json().catch(() => ({}));
        if (r1.ok && (d1.status === true || d1.success === true || !d1.error)) {
          sent = true;
        } else if (d1.message) {
          errorMsg = d1.message;
        }
      } catch (e) {}

      // 2. Fallback to /auth/email/warning
      if (!sent) {
        try {
          const r2 = await fetch(`${baseUrl}/auth/email/warning`, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
          });
          const d2 = await r2.json().catch(() => ({}));
          if (r2.ok && (d2.status === true || d2.success === true || !d2.error)) {
            sent = true;
          } else if (d2.message) {
            errorMsg = d2.message;
          }
        } catch (e) {}
      }

      // 3. Fallback to /auth/email/pioneer
      if (!sent) {
        try {
          const r3 = await fetch(`${baseUrl}/auth/email/pioneer`, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
          });
          const d3 = await r3.json().catch(() => ({}));
          if (r3.ok && (d3.status === true || d3.success === true || !d3.error)) {
            sent = true;
          } else if (d3.message) {
            errorMsg = d3.message;
          }
        } catch (e) {}
      }

      // 4. Fallback to /users/send-email
      if (!sent) {
        try {
          const r4 = await fetch(`${baseUrl}/users/send-email`, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
          });
          const d4 = await r4.json().catch(() => ({}));
          if (r4.ok && (d4.status === true || d4.success === true || !d4.error)) {
            sent = true;
          } else if (d4.message) {
            errorMsg = d4.message;
          }
        } catch (e) {}
      }

      if (sent) {
        toast.success(`Email sent successfully to ${selectedUserForMail.email}!`);
        setMailModalOpen(false);
      } else {
        toast.error(errorMsg || "Failed to send email");
      }
    } catch (err) {
      toast.error(err.message || "Failed to send email");
    } finally {
      setMailLoading(false);
    }
  };

  const handleOpenDeleteModal = (user) => {
    setSelectedUserForDelete(user);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedUserForDelete) return;
    setDeleteLoading(true);
    const targetId = selectedUserForDelete.userId || selectedUserForDelete.id;
    const token = localStorage.getItem("token");

    try {
      let res = await fetch(`${baseUrl}/users/delete/${targetId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      let result = await res.json().catch(() => ({}));

      if (!res.ok || result?.status === false) {
        res = await fetch(`${baseUrl}/users/${targetId}`, {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        });
        result = await res.json().catch(() => ({}));
      }

      if (res.ok && result?.status !== false) {
        toast.success(result?.message || `User #${targetId} deleted successfully`);
        setUsers((prev) => prev.filter((u) => (u.userId || u.id) !== targetId));
        setTotalUsers((prev) => Math.max(0, prev - 1));
        setDeleteModalOpen(false);
        setSelectedUserForDelete(null);
      } else {
        toast.error(result?.message || "Failed to delete user");
      }
    } catch (err) {
      toast.error(err.message || "Failed to delete user");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearch(value);
    setCurrentPage(1);
    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => setDebouncedSearch(value), 500);
  };

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, filter, currentPage]);

  const fetchUsers = async () => {
    setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${baseUrl}/users/list?search=${debouncedSearch}&status=${filter}&page=${currentPage}&perPage=${itemsPerPage}`,
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
      );
      const result = await response.json();
      if (result.status === true && Array.isArray(result.data)) {
        setUsers(result.data);
        setTotalUsers(result.pagination?.total || 0);
      } else {
        throw new Error(result?.message || "Failed to fetch user list");
      }
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  const totalPages = Math.ceil(totalUsers / itemsPerPage);
  const maxVisiblePages = 5;
  let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
  let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
  if (endPage - startPage < maxVisiblePages - 1) startPage = Math.max(1, endPage - maxVisiblePages + 1);

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  };

  return (
    <div className="flex bg-gray-100 min-h-screen">
      <Toaster position="top-right" />
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Navbar />
        <div className="p-5 sm:p-8 space-y-6">

          {/* Breadcrumbs & Heading */}
          <div className="space-y-3">
            <div className="flex items-center flex-wrap gap-1.5 text-xs text-gray-500 font-medium">
              <Link
                to="/dashboard"
                className="hover:text-black hover:underline transition flex items-center gap-1"
              >
                Dashboard
              </Link>
              <FiChevronRight size={12} className="text-gray-400 flex-shrink-0" />
              <span className="text-gray-900 font-bold bg-[#ffffcc] px-2 py-0.5 rounded">
                All Users
              </span>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => {
                    if (window.history.length > 1) {
                      navigate(-1);
                    } else {
                      navigate("/dashboard");
                    }
                  }}
                  className="p-2.5 bg-white hover:bg-gray-50 border border-gray-200 rounded-xl text-gray-700 hover:text-black transition shadow-xs cursor-pointer flex items-center justify-center"
                  title="Back to Previous Page"
                  aria-label="Back"
                >
                  <FiArrowLeft size={18} />
                </button>
                <div>
                  <h1 className="text-2xl font-black text-gray-900 tracking-tight">All Users</h1>
                  <p className="text-xs text-gray-500 mt-0.5">Manage all registered users on the network</p>
                </div>
              </div>

              <div className="bg-white border border-gray-100 shadow-sm rounded-xl px-4 py-2.5 text-center hidden sm:block">
                <p className="text-[10px] uppercase font-bold text-gray-400">Total Users</p>
                <p className="text-lg font-black text-gray-800">{totalUsers.toLocaleString()}</p>
              </div>
            </div>
          </div>

          {/* Search bar & Actions */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 flex flex-wrap items-center justify-between gap-3">
            <div className="relative w-full sm:w-80">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={15} />
              <input
                type="text"
                placeholder="Search by username or email..."
                value={search}
                onChange={handleSearchChange}
                className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#ffff00]"
              />
            </div>

            <div className="flex items-center gap-2.5">
              <button
                onClick={() => handleOpenPushModal(null)}
                className="flex items-center gap-2 px-3.5 py-2.5 bg-[#ffffcc] hover:bg-[#ffff00] text-black rounded-lg text-sm font-semibold transition shadow-2xs cursor-pointer"
                title="Broadcast push notification to users"
              >
                <FiSend size={15} />
                <span>Send Push</span>
              </button>

              <button
                onClick={() => navigate("/duplicate-account")}
                className="flex items-center gap-2 px-3.5 py-2.5 bg-red-50 hover:bg-red-100 text-red-700 rounded-lg text-sm font-medium transition cursor-pointer"
                title="View accounts sharing identical device UUIDs"
              >
                <FiCopy size={15} />
                <span>Duplicate Accounts</span>
              </button>
            </div>
          </div>

          {/* Content */}
          {loading ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 flex justify-center items-center py-24">
              <div className="w-8 h-8 border-4 border-[#ffff00] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 text-red-600 rounded-xl p-5 text-sm">
              {error}
            </div>
          ) : (
            <>
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                {/* Table header row */}
                <div className="px-5 py-3.5 border-b border-gray-100 flex items-center justify-between">
                  <p className="text-sm font-semibold text-gray-700">All Users</p>
                  <p className="text-xs text-gray-400">
                    {users.length > 0
                      ? `Showing ${(currentPage - 1) * itemsPerPage + 1}–${Math.min(currentPage * itemsPerPage, totalUsers)} of ${totalUsers}`
                      : "No results"}
                  </p>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 text-left text-xs text-gray-400 uppercase tracking-wide">
                        <th className="px-5 py-3 w-10">#</th>
                        <th className="px-5 py-3">User</th>
                        <th className="px-5 py-3 hidden md:table-cell">Email</th>
                        <th className="px-5 py-3 text-right hidden sm:table-cell">Coins</th>
                        <th className="px-5 py-3 text-center hidden lg:table-cell">Mining</th>
                        <th className="px-5 py-3 text-center hidden lg:table-cell">Strike</th>
                        <th className="px-5 py-3 hidden xl:table-cell">Referral</th>
                        <th className="px-5 py-3 hidden xl:table-cell">Joined</th>
                        <th className="px-5 py-3 text-center w-24">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {users.length === 0 ? (
                        <tr>
                          <td colSpan={9} className="py-16 text-center">
                            <div className="flex flex-col items-center gap-2 text-gray-400">
                              <FiUsers size={32} className="text-gray-300" />
                              <p className="text-sm">No users found</p>
                              {search && <p className="text-xs">Try a different search term</p>}
                            </div>
                          </td>
                        </tr>
                      ) : (
                        users.map((user, index) => {
                          const initials = (user.username || "U")[0].toUpperCase();
                          const avatarCls = getAvatarColor(user.username || "");
                          return (
                            <tr key={user.userId} className="hover:bg-gray-50 transition">
                              <td className="px-5 py-3.5 text-gray-400 text-xs">
                                {(currentPage - 1) * itemsPerPage + index + 1}
                              </td>
                              <td className="px-5 py-3.5">
                                <div className="flex items-center gap-3">
                                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${avatarCls}`}>
                                    {initials}
                                  </div>
                                  <div className="min-w-0">
                                    <p className="font-medium text-gray-800 truncate max-w-[130px]">
                                      {user.username || "—"}
                                    </p>
                                    <p className="text-xs text-gray-400 md:hidden truncate max-w-[130px]">
                                      {user.email}
                                    </p>
                                  </div>
                                </div>
                              </td>
                              <td className="px-5 py-3.5 text-gray-500 hidden md:table-cell">
                                <span className="block truncate max-w-[200px]">{user.email}</span>
                              </td>
                              <td className="px-5 py-3.5 text-right font-semibold text-gray-700 hidden sm:table-cell whitespace-nowrap">
                                {parseFloat(user.coin || 0).toFixed(2)}
                              </td>
                              <td className="px-5 py-3.5 text-center hidden lg:table-cell">
                                <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                                  user.isMiningActive ? "bg-green-100 text-green-600" : "bg-gray-100 text-gray-500"
                                }`}>
                                  {user.isMiningActive ? "Active" : "Inactive"}
                                </span>
                              </td>
                              <td className="px-5 py-3.5 text-center hidden lg:table-cell">
                                <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                                  user.strike > 0 ? "bg-orange-100 text-orange-600" : "bg-gray-100 text-gray-500"
                                }`}>
                                  {user.strike ?? 0}
                                </span>
                              </td>
                              <td className="px-5 py-3.5 font-mono text-xs text-gray-400 hidden xl:table-cell">
                                {user.referralCode || "—"}
                              </td>
                              <td className="px-5 py-3.5 text-gray-500 whitespace-nowrap text-xs hidden xl:table-cell">
                                {formatDate(user.createdAt)}
                              </td>
                              <td className="px-5 py-3.5 text-center">
                                <div className="flex items-center justify-center gap-1.5">
                                  <button
                                    onClick={() => navigate(`/user/${user.userId || user.id}/coin-history`)}
                                    className="p-1.5 rounded-lg text-amber-600 hover:bg-amber-50 transition"
                                    title="View Coin History Ledger"
                                  >
                                    🪙
                                  </button>
                                  <button
                                    onClick={() => handleOpenPushModal(user)}
                                    className="p-1.5 rounded-lg text-amber-700 hover:bg-amber-50 transition"
                                    title="Send Push Notification to User"
                                  >
                                    <FiBell size={16} />
                                  </button>
                                  <button
                                    onClick={() => handleOpenMailModal(user)}
                                    className="p-1.5 rounded-lg text-blue-600 hover:bg-blue-50 transition"
                                    title="Send Email to User"
                                  >
                                    <FiMail size={16} />
                                  </button>
                                  <button
                                    onClick={() => navigate(`/user/${user.userId || user.id}`)}
                                    className="p-1.5 rounded-lg text-[#b8b800] hover:bg-[#ffffe0] transition"
                                    title="View Details"
                                  >
                                    <FiEye size={16} />
                                  </button>
                                  <button
                                    onClick={() => handleOpenDeleteModal(user)}
                                    className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 hover:text-red-700 transition"
                                    title="Delete User"
                                  >
                                    <FiTrash2 size={16} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center gap-2 items-center">
                  <button
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                    className="px-3 py-2 border rounded-lg bg-white hover:bg-gray-50 disabled:opacity-40 text-sm"
                  >
                    &laquo;
                  </button>
                  {Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i).map((pg) => (
                    <button
                      key={pg}
                      onClick={() => setCurrentPage(pg)}
                      className={`px-4 py-2 border rounded-lg text-sm transition ${
                        currentPage === pg ? "bg-[#ffff00] text-black border-[#ffff00]" : "bg-white hover:bg-gray-50"
                      }`}
                    >
                      {pg}
                    </button>
                  ))}
                  <button
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                    className="px-3 py-2 border rounded-lg bg-white hover:bg-gray-50 disabled:opacity-40 text-sm"
                  >
                    &raquo;
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Delete User Confirmation Modal */}
      {deleteModalOpen && selectedUserForDelete && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          onClick={() => !deleteLoading && setDeleteModalOpen(false)}
        >
          <div
            className="bg-white w-full max-w-md rounded-2xl shadow-2xl border border-gray-100 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 text-center">
              <div className="w-14 h-14 rounded-2xl bg-red-50 text-red-600 flex items-center justify-center mx-auto mb-4 border border-red-100">
                <FiTrash2 size={26} />
              </div>

              <h3 className="text-lg font-bold text-gray-800 mb-1">
                Delete User Account?
              </h3>
              <p className="text-xs text-gray-500 mb-4">
                Are you sure you want to permanently delete user{" "}
                <strong className="text-gray-800 font-semibold">
                  {selectedUserForDelete.username || selectedUserForDelete.email || "Account"}
                </strong>{" "}
                (#{selectedUserForDelete.userId || selectedUserForDelete.id})?
              </p>

              {/* User Details Summary */}
              <div className="bg-gray-50 border border-gray-100 rounded-xl p-3.5 text-xs text-left mb-4 space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">User ID:</span>
                  <span className="font-bold text-gray-800">
                    #{selectedUserForDelete.userId || selectedUserForDelete.id}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Username:</span>
                  <span className="font-semibold text-gray-700">
                    {selectedUserForDelete.username || "—"}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Email:</span>
                  <span className="font-mono text-gray-700 truncate max-w-[200px]">
                    {selectedUserForDelete.email || "—"}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Total Coins:</span>
                  <span className="font-bold text-amber-600">
                    {parseFloat(selectedUserForDelete.coin || 0).toFixed(2)} ZX
                  </span>
                </div>
              </div>

              <div className="bg-red-50 border border-red-100 rounded-xl p-3 text-xs text-red-700 text-left mb-5 flex items-start gap-2">
                <FiAlertCircle size={16} className="flex-shrink-0 mt-0.5 text-red-500" />
                <span>
                  This action is permanent and irreversible. The user's account, mining progress, coins, and referral records will be removed.
                </span>
              </div>

              <div className="flex items-center justify-end gap-3">
                <button
                  type="button"
                  disabled={deleteLoading}
                  onClick={() => setDeleteModalOpen(false)}
                  className="w-full py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-xl transition disabled:opacity-50"
                >
                  Cancel
                </button>

                <button
                  type="button"
                  disabled={deleteLoading}
                  onClick={handleConfirmDelete}
                  className="w-full py-2.5 text-sm font-bold text-white bg-red-600 hover:bg-red-700 rounded-xl shadow transition disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {deleteLoading ? (
                    <>
                      <FiRefreshCw size={14} className="animate-spin" /> Deleting...
                    </>
                  ) : (
                    <>
                      <FiTrash2 size={14} /> Yes, Delete
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Send Email Modal */}
      {mailModalOpen && selectedUserForMail && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-xs flex items-center justify-center z-50 p-4"
          onClick={() => !mailLoading && setMailModalOpen(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in-95 duration-150"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-blue-100 text-blue-800 flex items-center justify-center font-bold">
                  <FiMail size={16} />
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-900">Send Email to User</h3>
                  <p className="text-[11px] text-gray-500">{selectedUserForMail.email}</p>
                </div>
              </div>
              <button
                onClick={() => !mailLoading && setMailModalOpen(false)}
                disabled={mailLoading}
                className="text-gray-400 hover:text-gray-600 p-1 rounded-lg"
              >
                <FiX size={18} />
              </button>
            </div>

            <form onSubmit={handleSendEmail} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">Subject</label>
                <input
                  type="text"
                  value={mailSubject}
                  onChange={(e) => setMailSubject(e.target.value)}
                  className="w-full px-3.5 py-2 border border-gray-200 rounded-lg text-xs font-medium focus:outline-none focus:ring-2 focus:ring-blue-400"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">Message Content</label>
                <textarea
                  rows={6}
                  value={mailContent}
                  onChange={(e) => setMailContent(e.target.value)}
                  className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-xs leading-relaxed focus:outline-none focus:ring-2 focus:ring-blue-400"
                  required
                />
              </div>

              <div className="pt-3 border-t border-gray-100 flex items-center justify-end gap-2.5">
                <button
                  type="button"
                  onClick={() => setMailModalOpen(false)}
                  disabled={mailLoading}
                  className="px-4 py-2 text-xs font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={mailLoading || !mailSubject.trim() || !mailContent.trim()}
                  className="px-5 py-2 text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition flex items-center gap-1.5 shadow disabled:opacity-50"
                >
                  {mailLoading ? (
                    <>
                      <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Sending...</span>
                    </>
                  ) : (
                    <>
                      <FiSend size={13} />
                      <span>Send Email</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Send Push Notification Modal */}
      <SendPushModal
        isOpen={pushModalOpen}
        onClose={() => setPushModalOpen(false)}
        defaultUserId={
          selectedUserForPush
            ? selectedUserForPush.userId || selectedUserForPush.id
            : null
        }
        defaultUsername={
          selectedUserForPush?.username || selectedUserForPush?.email || null
        }
        initialMode={pushModalMode}
      />
    </div>
  );
};

export default UserList;

